// components/widgets/widget-expand-modal.js
// ═══════════════════════════════════════════════════════════
// WIDGET EXPAND MODAL — focused reading view for dense widgets
// ═══════════════════════════════════════════════════════════
// When a widget's registry entry has body.expandable = true, the
// card footer shows an ⤢ Expand button. Clicking it opens this
// modal — a ~70vw × 85vh overlay that re-renders the widget
// content for focused reading without the dashboard in view.
//
// Closes on: click outside, Esc, or the X button.
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';

export default function WidgetExpandModal({
  open,
  onClose,
  widgetKey,
  title,
  children,
}) {
  const contentRef = useRef(null);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Focus trap — simple: focus the content on open
  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.focus?.();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="ws-widget-expand-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`expand-title-${widgetKey}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="ws-widget-expand-modal" ref={contentRef} tabIndex={-1}>
        <header className="ws-widget-expand-header">
          <h2 id={`expand-title-${widgetKey}`} className="ws-widget-expand-title">
            {title}
          </h2>
          <button
            type="button"
            className="ws-widget-expand-close"
            onClick={onClose}
            aria-label="Close expanded view (Esc)"
            title="Close (Esc)"
          >
            ✕
          </button>
        </header>
        <div className="ws-widget-expand-body">
          {children}
        </div>
      </div>
    </div>
  );
}
